import { FleetDataFilter } from './FleetDataFilter';
import FleetWidget from './FleetWidget';
import { FleetDashboardIcon } from 'components/fleet-management/CustomFleetDashboardIcon';
import { PageOption } from 'models/enums/fleet-management';

export enum DashboardViewType {
  DEALER_OVERVIEW = 'DEALER_OVERVIEW',
  FLEET_OVERVIEW = 'FLEET_OVERVIEW',
  ORBIT = 'ORBIT',
  FLEET_HEALTH = 'FLEET_HEALTH',
  FLEET_PERFORMANCE = 'FLEET_PERFORMANCE',
}
interface FleetDashboard {
  id: string;
  name: string;
  description: string;
  icon: FleetDashboardIcon;
  widgets: FleetWidget[];
  isDefault: boolean;
  viewType?: DashboardViewType;
  filters?: FleetDataFilter[];
}

export default FleetDashboard;

/**
 * The built-in pages, each with the view type a saved setup of it is stored as. A saved view keeps
 * only filters and opens its page with them; an Orbit dashboard is configured separately and has no
 * built-in page of its own.
 */
export interface BuiltInPage {
  page: PageOption;
  viewType: DashboardViewType;
  icon: FleetDashboardIcon;
  titleKey: string;
}

export const BUILT_IN_PAGES: BuiltInPage[] = [
  {
    page: PageOption.DealerOverview,
    viewType: DashboardViewType.DEALER_OVERVIEW,
    icon: FleetDashboardIcon.StatisticsIcon,
    titleKey: 'fleet-management-dealer-overview',
  },
  {
    page: PageOption.FleetOverview,
    viewType: DashboardViewType.FLEET_OVERVIEW,
    icon: FleetDashboardIcon.QueryStatisticsIcon,
    titleKey: 'fleet-management-fleet-overview',
  },
  {
    page: PageOption.FleetHealth,
    viewType: DashboardViewType.FLEET_HEALTH,
    icon: FleetDashboardIcon.HealthChangesIcon,
    titleKey: 'fleet-management-fleet-health',
  },
  {
    page: PageOption.FleetPerformance,
    viewType: DashboardViewType.FLEET_PERFORMANCE,
    icon: FleetDashboardIcon.PerformanceChangesIcon,
    titleKey: 'fleet-management-fleet-performance',
  },
];

export const builtInPageFor = (page: PageOption | null | undefined) =>
  BUILT_IN_PAGES.find((p) => p.page === page);

/** The page a saved dashboard opens: its built-in page, or Orbit for everything else. */
export const pageForViewType = (viewType: DashboardViewType | undefined): PageOption =>
  BUILT_IN_PAGES.find((p) => p.viewType === viewType)?.page ?? PageOption.Orbit;

/** Can a dashboard of this view type be named and re-filtered from its page? */
export const isSavedViewType = (viewType: DashboardViewType | undefined) =>
  BUILT_IN_PAGES.some((p) => p.viewType === viewType);

/**
 * The built-in page the `DashboardView` query parameter asks for, or Fleet Health when it names
 * none. Dealer Overview is only honoured for the roles that may see it.
 */
export const resolveGeneralPage = (
  queryDashboardView: string | null,
  canSeeDealerOverview: boolean,
): PageOption =>
  BUILT_IN_PAGES.find(
    (p) =>
      p.page === queryDashboardView &&
      (p.page !== PageOption.DealerOverview || canSeeDealerOverview),
  )?.page ?? PageOption.FleetHealth;
